import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUp, BookOpen, ChevronRight, FileText, Globe, Info, ShieldCheck } from "lucide-react";
import logo from "../assets/app/logo.png";

const BRAND = "BEAUTABLES";
const SITE = "beautables.app";               // change if your domain is different
const SITE_URL = `https://${SITE}`;
const HANDLE = "beautables";

const ROUTES = {
  about: "/about-us",
  story: "/story",
  privacy: "/privacy",
  terms: "/terms",
};

const SOCIALS = [
  { key: "instagram", label: "Instagram", href: `https://www.instagram.com/${HANDLE}` },
  { key: "facebook", label: "Facebook", href: `https://www.facebook.com/${HANDLE}` },
  { key: "tiktok", label: "TikTok", href: `https://www.tiktok.com/@${HANDLE}` },
  { key: "x", label: "X", href: `https://x.com/${HANDLE}` },
  { key: "linkedin", label: "LinkedIn", href: `https://www.linkedin.com/company/${HANDLE}` },
  { key: "youtube", label: "YouTube", href: `https://www.youtube.com/@${HANDLE}` },
] as const;

const SocialIcon: React.FC<{ k: (typeof SOCIALS)[number]["key"] }> = ({ k }) => {
  const common = { width: 20, height: 20, viewBox: "0 0 24 24", "aria-hidden": true } as const;
  const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  if (k === "instagram")
    return (<svg {...common} {...stroke}><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.2" cy="6.8" r="0.8" fill="currentColor" /></svg>);
  if (k === "facebook")
    return (<svg {...common} {...stroke}><path d="M14 8h3V4h-3a4 4 0 0 0-4 4v3H7v4h3v7h4v-7h3l1-4h-4V8z" /></svg>);
  if (k === "tiktok")
    return (<svg {...common} {...stroke}><path d="M16 3v10.5a4.5 4.5 0 1 1-4.5-4.5" /><path d="M16 3c0 2.6 2 4.6 4.5 4.6" /></svg>);
  if (k === "x")
    return (<svg {...common} {...stroke}><path d="M4 4l16 16" /><path d="M20 4L4 20" /></svg>);
  if (k === "linkedin")
    return (<svg {...common} {...stroke}><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M8 10.5V16" /><circle cx="8" cy="7.6" r="0.7" fill="currentColor" /><path d="M12 16v-5.5" /><path d="M12 13c0-1.6 1-2.7 2.5-2.7S17 11.4 17 13v3" /></svg>);
  return (<svg {...common} {...stroke}><rect x="2.5" y="5.5" width="19" height="13" rx="4" /><path d="M10 9.5v5l4.5-2.5-4.5-2.5z" /></svg>);
};

/* ═══════════ SHOP PALETTE ON SOFT LIME GREEN ═══════════ */
const NEON = "#C0DC6C";
const NEON_HI = "#DDEFA3";
const NEON_LO = "#A6C851";
const INK = "#000";
const YEL = "#F8DA43";
const RASP = "#FB2564";
const RED = "#E10000";

/* hover colour sets cycle yellow · raspberry · white · red (lime would vanish on the green) */
const HV = [
  { g: `linear-gradient(180deg,#FFF27A 0%,${YEL} 52%,#F2B800 100%)`, s: YEL, f: INK },
  { g: `linear-gradient(180deg,#FF6B93 0%,${RASP} 52%,#C4114A 100%)`, s: RASP, f: "#fff" },
  { g: `linear-gradient(180deg,#fff 0%,#f4f4f4 52%,#d9d9d9 100%)`, s: "#fff", f: INK },
  { g: `linear-gradient(180deg,#FF5A5A 0%,${RED} 52%,#B30000 100%)`, s: RED, f: "#fff" },
];
const cycle = (sel: string) =>
  HV.map((h, i) => `${sel}:nth-child(4n+${i + 1}){ --hg:${h.g}; --hs:${h.s}; --hf:${h.f}; }`).join("\n");

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Jost:wght@300;400;500;600;700&display=swap');
.ft, .ft *, .ft *::before, .ft *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.ft{ --hg:${HV[0].g}; --hs:${HV[0].s}; --hf:${HV[0].f};
  position:relative; margin-top:56px; color:${INK}; padding:0 20px calc(28px + env(safe-area-inset-bottom)); overflow:hidden;
  background:
    radial-gradient(620px 320px at 14% 0%, rgba(255,255,255,.55), transparent 70%),
    radial-gradient(520px 320px at 92% 100%, rgba(140,178,48,.5), transparent 70%),
    linear-gradient(180deg, ${NEON_HI} 0%, ${NEON} 30%, ${NEON} 70%, ${NEON_LO} 100%);
  border-top:3px solid ${INK}; border-radius:44px 44px 0 0;
  box-shadow:inset 0 4px 0 rgba(255,255,255,.7), inset 0 -10px 24px rgba(0,0,0,.12); }
.ft a{ color:inherit; text-decoration:none; }
.ft button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.ft a:focus-visible, .ft button:focus-visible{ outline:3px solid ${INK}; outline-offset:3px; }
.ft-inner{ position:relative; z-index:1; max-width:1100px; margin:0 auto; padding-top:56px; display:flex; flex-direction:column; gap:46px; }

.ft-grid{ display:grid; grid-template-columns:1.6fr 1fr 1fr; gap:36px; }
.ft-logo{ height:56px; max-width:220px; object-fit:contain; display:block; cursor:pointer; filter:drop-shadow(0 3px 0 rgba(0,0,0,.25)); }
.ft-brand p{ margin:18px 0 20px; font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:600; line-height:1.15; max-width:320px; }

/* shared glossy 3D pill / orb recipe */
.ft-site, .ft-links a, .ft-social a, .ft-top{
  position:relative; isolation:isolate; overflow:hidden; border:1px solid ${INK}; color:${INK};
  background:linear-gradient(180deg,#fff 0%,#f1f1f1 55%,#dcdcdc 100%);
  box-shadow:inset 0 2px 2px #fff, inset 0 -4px 6px rgba(0,0,0,.12), 0 5px 0 ${INK}, 0 12px 16px -6px rgba(0,0,0,.45);
  transition:background .25s, color .25s, transform .15s, box-shadow .15s, border-color .25s; }
.ft-site::before, .ft-links a::before, .ft-social a::before, .ft-top::before{
  content:""; position:absolute; z-index:-1; left:8%; right:8%; top:3px; height:46%; border-radius:999px; pointer-events:none;
  background:linear-gradient(180deg,rgba(255,255,255,.95),rgba(255,255,255,.1)); }
.ft-site:hover, .ft-links a:hover, .ft-social a:hover, .ft-top:hover{
  background:var(--hg); color:var(--hf); transform:translateY(-2px);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.75), inset 0 -4px 6px rgba(0,0,0,.15), 0 7px 0 ${INK}, 0 16px 20px -6px rgba(0,0,0,.5); }
.ft-site:active, .ft-links a:active, .ft-social a:active, .ft-top:active{
  transform:translateY(4px); box-shadow:inset 0 2px 2px rgba(255,255,255,.6), inset 0 -3px 5px rgba(0,0,0,.15), 0 1px 0 ${INK}, 0 4px 8px -4px rgba(0,0,0,.5); }

.ft-site{ display:inline-flex; align-items:center; gap:10px; height:46px; padding:0 22px; border-radius:999px; font-size:11.5px; font-weight:600; letter-spacing:.2em; text-transform:uppercase; }
.ft-handle{ margin:26px 0 14px; font-size:12px; font-weight:600; letter-spacing:.24em; text-transform:uppercase; }
.ft-social{ display:flex; flex-wrap:wrap; gap:12px; }
.ft-social a{ width:46px; height:46px; border-radius:50%; display:grid; place-items:center; }
${cycle(".ft-social > a")}
${cycle(".ft-links > li")}

.ft-col h4{ margin:4px 0 20px; font-family:'Cormorant Garamond',serif; font-weight:700; font-size:32px; line-height:1; }
.ft-links{ list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:14px; }
.ft-links a{ display:flex; align-items:center; gap:12px; height:52px; padding:0 20px; border-radius:999px; font-size:12px; font-weight:600; letter-spacing:.2em; text-transform:uppercase; }
.ft-lico{ display:flex; }
.ft-links a > span:nth-child(2){ flex:1; }
.ft-arrow{ transition:transform .25s; }
.ft-links a:hover .ft-arrow{ transform:translateX(4px); }

/* wordmark: white glossy face, black 3D extrusion */
.ft-mark{ position:relative; text-align:center; font-family:'Cormorant Garamond',serif; font-weight:700; text-transform:uppercase; font-size:clamp(34px,12.5vw,170px); line-height:1.1; letter-spacing:.06em; margin:0; font-synthesis:none;
  color:transparent; -webkit-text-fill-color:transparent; -webkit-background-clip:text; background-clip:text; background-repeat:no-repeat;
  background-image:linear-gradient(105deg,transparent 42%,rgba(255,255,255,1) 50%,transparent 58%),linear-gradient(180deg,#ffffff 0%,#f4f4f4 40%,#d6d6d6 50%,#f0f0f0 100%);
  background-size:60% 100%,100% 100%; background-position:-150% 0,0 0; animation:ftShine 5s ease-in-out infinite;
  filter:drop-shadow(0 1px 0 #000) drop-shadow(0 2px 0 #000) drop-shadow(0 3px 0 #000) drop-shadow(0 4px 0 #000) drop-shadow(0 5px 0 #000) drop-shadow(0 6px 0 #000) drop-shadow(0 7px 0 #000) drop-shadow(0 16px 14px rgba(0,0,0,.35)); }
@keyframes ftShine{ 0%{ background-position:-150% 0,0 0; } 60%,100%{ background-position:250% 0,0 0; } }

.ft-bottom{ display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; padding-top:22px; border-top:2px solid ${INK}; font-size:12px; font-weight:600; letter-spacing:.16em; text-transform:uppercase; }
.ft-top{ width:46px; height:46px; border-radius:50%; display:grid; place-items:center; }

@media (max-width:760px){
  .ft{ border-radius:30px 30px 0 0; }
  .ft-grid{ grid-template-columns:1fr 1fr; }
  .ft-brand{ grid-column:1 / -1; }
  .ft-links a{ padding:0 14px; letter-spacing:.14em; font-size:11px; }
  .ft-bottom{ justify-content:center; text-align:center; }
}
@media (prefers-reduced-motion: reduce){
  .ft *, .ft *::before, .ft *::after{ animation:none !important; transition:none !important; }
}
`;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const go = (to: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(to);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const company = [
    { label: "About us", to: ROUTES.about, Icon: Info },
    { label: "Our story", to: ROUTES.story, Icon: BookOpen },
  ];
  const legal = [
    { label: "Privacy", to: ROUTES.privacy, Icon: ShieldCheck },
    { label: "Terms", to: ROUTES.terms, Icon: FileText },
  ];

  const renderLinks = (list: typeof company) => (
    <ul className="ft-links">
      {list.map(({ label, to, Icon }) => (
        <li key={to}>
          <a href={to} onClick={go(to)}>
            <span className="ft-lico"><Icon size={16} /></span>
            <span>{label}</span>
            <ChevronRight size={16} className="ft-arrow" />
          </a>
        </li>
      ))}
    </ul>
  );

  return (
    <footer className="ft">
      <style>{CSS}</style>

      <div className="ft-inner">
        <div className="ft-grid">
          <div className="ft-brand">
            <img className="ft-logo" src={logo} alt={BRAND} onClick={() => navigate("/")} />
            <p>Beautiful things, made to be loved.</p>
            <a className="ft-site" href={SITE_URL} target="_blank" rel="noopener noreferrer">
              <Globe size={16} /> {SITE}
            </a>
            <div className="ft-handle">@{HANDLE}</div>
            <div className="ft-social" aria-label={`Follow ${BRAND}`}>
              {SOCIALS.map((s) => (
                <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label}>
                  <SocialIcon k={s.key} />
                </a>
              ))}
            </div>
          </div>

          <nav className="ft-col" aria-label="Company">
            <h4>Company</h4>
            {renderLinks(company)}
          </nav>

          <nav className="ft-col" aria-label="Legal">
            <h4>Legal</h4>
            {renderLinks(legal)}
          </nav>
        </div>

        <h2 className="ft-mark" aria-label={BRAND}>{BRAND}</h2>

        <div className="ft-bottom">
          <span>© {new Date().getFullYear()} {BRAND}. All rights reserved.</span>
          <button type="button" className="ft-top" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top"><ArrowUp size={17} /></button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;